"use client";

// The weekly letter, read natively.
//
// It used to be markdown, and markdown could only say "here is a feature" by
// embedding an image of a generated card whose whole content was the feature's
// NAME. Three pictures of three titles, and the sentences underneath them
// carried all the meaning. So the letter now travels as structure (`LetterData`)
// and renders here in the house language: the same tick rows the dispatch uses,
// and a quote that reads as a quote.
//
// The embed is ours on purpose. An X or Instagram widget drops a second design
// system into the middle of the page — its own font, its own card, its own blue
// — and the letter stops being one thing. So a post is a quote in innernet's
// type, the account and the platform's real mark beneath it, and a link out. It
// costs the like-count and buys a page that reads as itself.

import Link from "next/link";
import BrandIcon from "../BrandIcon";
import OrganismMark from "../brand/OrganismMark";
import { quiet } from "../../lib/company/quiet";
import { LETTER_LABELS } from "../../lib/newsletter/labels";
import type { DispatchItem, LetterData, LetterSaid } from "../../lib/company/types";

/** an external link leaves; an innernet one is a route. */
function Out({ href, children }: { href: string; children: React.ReactNode }) {
  const internal = href.startsWith("/") || href.startsWith("https://innernet.live");
  const to = href.replace(/^https:\/\/innernet\.live/, "");
  if (internal) return <Link className="co-open" href={to || "/"}>{children}</Link>;
  return (
    <a className="co-open" href={href} target="_blank" rel="noreferrer">
      {children}
    </a>
  );
}

function Block({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <section className="co-block">
      <div className="co-block__label">{label}</div>
      {children}
    </section>
  );
}

/** the one row shape — a tick, a name, its line, and maybe a door. */
function Row({ title, note, href, label }: { title: string; note?: string; href?: string; label?: string }) {
  return (
    <div className="co-item">
      <span className="co-item__tick" aria-hidden />
      <span className="co-item__t">
        {quiet(title)}
        {note && <span className="co-item__n">{quiet(note)}</span>}
        {href && (
          <span className="co-item__n">
            <Out href={href}>{label ?? "open it"}</Out>
          </span>
        )}
      </span>
    </div>
  );
}

function Said({ s }: { s: LetterSaid }) {
  return (
    <div className="co-said">
      {s.quote && <p className="co-said__q">{quiet(s.quote)}</p>}
      {s.note && <p className="co-said__n">{quiet(s.note)}</p>}
      <a className="co-said__by" href={s.url} target="_blank" rel="noreferrer">
        <span className="co-said__mark" aria-hidden>
          {/* our own posts are signed with netti; everyone else keeps their platform */}
          {s.ours ? <OrganismMark size={13} /> : <BrandIcon name={s.platform} size={13} />}
        </span>
        {s.handle ? quiet(s.handle) : quiet(s.platform)}
        <span className="co-said__on">on {quiet(s.platform)}</span>
      </a>
    </div>
  );
}

export default function LetterBody({ data }: { data: LetterData }) {
  const items = (list: DispatchItem[] | undefined) =>
    (list ?? []).map((it) =>
      typeof it === "string" ? { title: it, note: undefined } : { title: it.title, note: it.note },
    );

  return (
    <div>
      {/* the opener is the post's dek — the reader has already read it above */}
      {!!data.can_do?.length && (
        <Block label={LETTER_LABELS.can_do}>
          {data.can_do.map((f, i) => (
            <Row key={i} title={f.name} note={f.summary} href={f.href} />
          ))}
        </Block>
      )}

      {!!data.smoothed?.length && (
        <Block label={LETTER_LABELS.smoothed}>
          {items(data.smoothed).map((it, i) => (
            <Row key={i} title={it.title} note={it.note} />
          ))}
        </Block>
      )}

      {!!data.shipped?.length && (
        <Block label={LETTER_LABELS.shipped}>
          {data.shipped.map((r, i) => (
            <Row
              key={i}
              title={r.version ? `${r.version} — ${r.title}` : r.title}
              note={r.dek}
              href={r.href}
              label="read it"
            />
          ))}
        </Block>
      )}

      {!!data.wrote?.length && (
        <Block label={LETTER_LABELS.wrote}>
          {data.wrote.map((p, i) => (
            <Row key={i} title={p.title} note={p.dek} href={p.href} label="read it" />
          ))}
        </Block>
      )}

      {!!data.said?.length && (
        <Block label={LETTER_LABELS.said}>
          {data.said.map((s, i) => (
            <Said key={i} s={s} />
          ))}
        </Block>
      )}
    </div>
  );
}
